import React, {
    useEffect,
    useState,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { setFormDetailsQuery, useFormDetailsQuery } from '../../lib/Queries';
import Questions, { getIcon, useDeleteQuestion } from '../../lib/Questions';
import SidePanel from '../SidePanel.jsx';
import EditQuestionForm from './EditQuestionForm';
import useDelete from '../../lib/useDelete.jsx';
import Loading from '../Loading';
import { useBlocker } from 'react-router-dom';
import useBlockUnsavedChanges from '~/lib/useBlockUnsavedChanges.js';

/**
 *
 * @param question
 * @param setQuestion
 * @param formId
 * @param formData This is used as the basis for updating local cache. Requerying here from fromId would refetch and on slow networks potentially overwrite user updates.
 * @returns {JSX.Element}
 * @constructor
 */
const EditQuestionPanel = forwardRef(
    ({ question, setQuestion, formId, url }, ref) => {
        const { data: formData } = useFormDetailsQuery(formId);

        const [showEditPanel, setShowEditPanel] = useState(false);
        const { saveDelete, isDeleting } = useDeleteQuestion(formId, question);
        const { isSaving: isSavingQuestion, save } = useBusinessPostApi(
            `/forms/${formId}/sections/${question?.form_section_id}/questions/${question?.id}`,
        );

        const [label, setLabel] = useState(question?.label);
        const [type, setType] = useState(question?.type);
        const [settings, setSettings] = useState(question?.settings);
        const [renderKey, setRenderKey] = useState(null);

        const [origLabel, setOrigLabel] = useState(question?.label);
        const [origType, setOrigType] = useState(question?.type);
        const [origSettings, setOrigSettings] = useState(question?.settings);

        const questionSchema = Questions[question?.type] || {};

        function isDirty() {
            return (
                origLabel !== label ||
                origType !== type ||
                JSON.stringify(origSettings) !== JSON.stringify(settings)
            );
        }

        // Expose the isDirty method via ref
        useImperativeHandle(ref, () => ({
            isDirty,
        }));

        useBlockUnsavedChanges(showEditPanel && isDirty);

        useEffect(() => {
            if (question) {
                // Reset when the question changes
                // The local state fields...
                setLabel(question.label);
                setType(question.type);
                setSettings(question.settings);
                // To rollback on cancel...
                setOrigLabel(question.label);
                setOrigType(question.type);
                setOrigSettings(question.settings);
            }

            // Ensure re-render happens only after we set state
            setRenderKey(question?.id || 'none');
        }, [question?.id]);

        useEffect(() => {
            if (!showEditPanel) {
                setQuestion(null);
            }
        }, [showEditPanel]);

        function doDelete() {
            saveDelete().then(result => {
                setQuestion(null); // It's gone!
            });
        }

        function doUpdate(ev) {
            ev.preventDefault();

            save({ label: label, type: type, settings: settings }).then(
                result => {
                    setShowEditPanel(false);
                    setFormDetailsQuery(formId, result);
                },
            );
        }

        function updateLocalQuestionData(label, type, settings) {
            // Update question...
            //  formData.sections[].questions[]

            if (!formData || !formData.sections) {
                return;
            }

            const newFormData = { ...formData };
            newFormData.sections = newFormData.sections.map(section => {
                const newQuestions = (section.questions || []).map(q => {
                    if (q.id === question?.id) {
                        return {
                            ...q,
                            label: label,
                            type: type,
                            settings: settings,
                        };
                    } else {
                        return q;
                    }
                });
                return { ...section, questions: newQuestions };
            });

            setFormDetailsQuery(formId, newFormData);
        }

        useEffect(() => {
            if (!question) {
                setShowEditPanel(false);
            } else {
                setShowEditPanel(true);
            }
        }, [question?.id]);

        useEffect(() => {
            if (showEditPanel) {
                // Updating local data
                updateLocalQuestionData(label, type, settings);
            }
        }, [
            label,
            type,
            settings,
            showEditPanel,
            JSON.stringify(
                formData,
            ) /*always make sure we show the latest preview*/,
        ]);

        function cancelEditing() {
            // Rollback changes
            if (isDirty()) {
                if (
                    confirm(
                        'You have unsaved changes, do you want to discard them?',
                    )
                ) {
                    updateLocalQuestionData(origLabel, origType, origSettings);
                    setShowEditPanel(false);
                }
            } else {
                setShowEditPanel(false);
            }
        }

        const Icon = getIcon(questionSchema);

        return (
            <SidePanel
                onClose={cancelEditing}
                isOpen={showEditPanel}
                title={
                    <div className="flex items-center">
                        <Icon className="p-1 w-6 h-6 text-white bg-primary-700 rounded mr-2" />
                        {questionSchema.name}
                    </div>
                }
            >
                <form onSubmit={doUpdate} key={renderKey} className="pb-16">
                    <EditQuestionForm
                        label={label}
                        type={question?.type}
                        setLabel={setLabel}
                        settings={settings}
                        setSettings={setSettings}
                        formDetails={formData}
                        question={question}
                        url={url}
                    />
                    <div className="flex justify-between items-center fixed bottom-0 w-full pr-12 py-2 mt-6 bg-gray-100 bg-opacity-50">
                        <button type="submit" className="btn-save">
                            {isSavingQuestion ? 'Saving...' : 'Save'}
                        </button>

                        {!questionSchema.cannotDelete && (
                            <button
                                className="text-red-700 hover:text-red-800"
                                onClick={doDelete}
                                type="button"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        )}
                    </div>
                </form>
            </SidePanel>
        );
    },
);

export default EditQuestionPanel;
